import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import belowIcon from '../../../asset/icon/below.png';
import Divider from '../../../shared/Divider';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import SelectBox from '../../../shared/component/SelectBox';
import { UniversalCheckbox } from '../../../shared/component/UniversalCheckbox';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { getBtnName } from '../../../shared/constants/btnNames';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getOnBoardingMessage } from '../../../shared/constants/onBoardingMessages';
import { getDefaultOnBoardingData } from '../../../shared/converter/entityBuilder';
import {
  AlertPosition,
  AlertType,
  DividerType,
  ModalType,
  SignupArchiveType,
} from '../../../shared/enum/EnumRepository';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import { useValidation } from '../../../shared/hooks/useValidation';
import { useLoadingStore } from '../../../shared/store/loadingStore';
import { useOnBoardStore } from '../../../shared/store/onBoardStore';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import MoleculeAuthInput from '../molecules/MoleculeAuthInput';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

type ActivityTypeValue = SignupArchiveType | '';

const activityTypeOptions = Object.values(SignupArchiveType);
const activityTypeLabels: Record<SignupArchiveType, string> = {
  [SignupArchiveType.ARTIST]: 'Artist',
  [SignupArchiveType.CURATOR]: 'Curator',
  [SignupArchiveType.RESEARCHER]: 'Researcher',
  [SignupArchiveType.EDUCATOR]: 'Educator',
  [SignupArchiveType.GROUP]: 'Group',
};

const RegisterMobile: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { checkEmail, checkHandle, signUp } = useAuth();
  const setOnBoardingData = useOnBoardStore((state) => state.setOnBoardingData);
  const updateOnBoardingData = useOnBoardStore((state) => state.updateOnBoardingData);
  const { isLoading } = useLoadingStore();
  const { clearModal, setStandardModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const {
    isEmail,
    isValidHandle,
    validateHandle: getHandleError,
    PASSWORD_REGEX,
    explainPasswordIssues,
  } = useValidation();

  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activityType, setActivityType] = useState<SignupArchiveType | null>(null);
  const [handleError, setHandleError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [requiredOver14, setRequiredOver14] = useState(false);
  const [requiredTerms, setRequiredTerms] = useState(false);
  const [requiredPrivacy, setRequiredPrivacy] = useState(false);
  const [optionalPromotion, setOptionalPromotion] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const isAllRequiredAgree = requiredOver14 && requiredTerms && requiredPrivacy;

  useEffect(() => {
    setIsUserInput(Boolean(
      handle ||
      email ||
      password ||
      activityType ||
      requiredOver14 ||
      requiredTerms ||
      requiredPrivacy ||
      optionalPromotion
    ));
  }, [
    handle,
    email,
    password,
    activityType,
    requiredOver14,
    requiredTerms,
    requiredPrivacy,
    optionalPromotion,
    setIsUserInput,
  ]);

  useEffect(() => {
    setOnBoardingData(getDefaultOnBoardingData());
    modalRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, [setOnBoardingData]);

  const validateHandleValue = (value: string) => {
    if (value.length === 0) {
      setHandleError('');
      return true;
    }

    const error = getHandleError(value);
    setHandleError(error ?? '');
    return error == null;
  };

  const changeHandleInput = (value: string) => {
    const lowerValue = value.toLowerCase();
    validateHandleValue(lowerValue);
    setHandle(lowerValue);
  };

  const validateEmail = (value: string) => {
    if (value.length === 0) {
      setEmailError('');
      return true;
    }
    if (!isEmail(value)) {
      setEmailError(getOnBoardingMessage('VALIDATION_EMAIL_INVALID') as string);
      return false;
    }
    setEmailError('');
    return true;
  };

  const changeEmailInput = (value: string) => {
    validateEmail(value);
    setEmail(value);
  };

  const validatePassword = (value: string) => {
    if (value.length === 0) {
      setPasswordError('');
      return true;
    }
    if (!PASSWORD_REGEX.test(value)) {
      setPasswordError(explainPasswordIssues(value)[0] ?? getAlertMessage('CHECK_FORMAT'));
      return false;
    }
    setPasswordError('');
    return true;
  };

  const changePasswordInput = (value: string) => {
    validatePassword(value);
    setPassword(value);
  };

  const getActivityTypeLabel = (value: ActivityTypeValue) => {
    if (!value) return getOnBoardingMessage('STEP_TITLE_ACTIVITY_TYPE') as string;
    return activityTypeLabels[value];
  };

  const handleSubmit = async () => {
    if (isLoading > 0) return;

    if (!handle || !email || !password || !activityType) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return;
    }

    const valid = [
      validateHandleValue(handle),
      validateEmail(email),
      validatePassword(password),
    ].every(Boolean);

    if (!valid || !isValidHandle(handle)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('CHECK_FORMAT'),
      });
      return;
    }

    if (!isAllRequiredAgree) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return;
    }

    if (!useOnBoardStore.getState().onBoardingData) {
      setOnBoardingData(getDefaultOnBoardingData());
    }
    updateOnBoardingData({
      email,
      handle,
      signupArchiveType: activityType,
      verifiedEmail: false,
      verifiedHandle: false,
      works: [],
      isPreviewGuideMode: false,
      isAgreePolicy: true,
    });

    await checkEmail(email);
    if (!useOnBoardStore.getState().onBoardingData?.verifiedEmail) return;

    updateOnBoardingData({ verifiedHandle: false });
    await checkHandle(handle);
    if (!useOnBoardStore.getState().onBoardingData?.verifiedHandle) return;

    await signUp({
      email,
      password,
      handle,
      over14: requiredOver14,
      agreeTos: requiredTerms,
      agreePrivacy: requiredPrivacy,
      marketingEmailOptIn: optionalPromotion,
      signupArchiveType: activityType,
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (isLoading > 0) return;
    if (event.key === 'Enter' && event.target instanceof HTMLButtonElement) return;
    if (event.key === 'Enter') handleSubmit();
    if (event.key === 'Escape') clearModal();
  };

  const openLoginModal = () => {
    setStandardModal({
      modalType: ModalType.LOGIN,
      title: null,
      value: null,
      handleChange: () => {},
    });
  };

  const openPolicy = (event: React.MouseEvent<HTMLSpanElement>, url: string) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <RegisterMobileComp ref={modalRef} onKeyDown={handleKeyDown} tabIndex={-1}>
      <MainContainer>
        <TitleWrapper>
          <Title>{getBtnName('REGISTER')}</Title>
          <UniversalIconBtn
            size="fit"
            border={false}
            isTransparent
            backgroundImg={belowIcon}
            onClick={() => clearModal(400)}
          />
        </TitleWrapper>

        <FieldsContainer>
          <FieldGroup>
            <MoleculeAuthInput
              name={getOnBoardingMessage('STEP_TITLE_PROFILE_URL') as string}
              type="text"
              inputName="handle"
              autoComplete="nickname"
              value={handle}
              handleChange={changeHandleInput}
              err={handleError || null}
              placeholder="url_name"
              helperText={(
                <>
                  www.architrave.work/@<HighlightText>{handle}</HighlightText>
                </>
              )}
              reserveFeedbackSpace
            />
          </FieldGroup>

          <FieldGroup>
            <MoleculeAuthInput
              name={getModalMessage('AUTH_EMAIL_LABEL')}
              type="email"
              inputName="email"
              autoComplete="email"
              value={email}
              handleChange={changeEmailInput}
              err={emailError || null}
              placeholder="username@email.com"
              reserveFeedbackSpace
            />
          </FieldGroup>

          <FieldGroup>
            <MoleculeAuthInput
              name={getModalMessage('AUTH_PASSWORD_LABEL')}
              inputName="password"
              autoComplete="new-password"
              value={password}
              handleChange={changePasswordInput}
              err={passwordError || null}
              placeholder={getModalMessage('AUTH_PASSWORD_LABEL')}
              reserveFeedbackSpace
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>{getOnBoardingMessage('STEP_TITLE_ACTIVITY_TYPE')}</FieldLabel>
            <ActivitySelectWrapper>
              <SelectBox<ActivityTypeValue>
                value={activityType ?? ''}
                options={activityTypeOptions}
                handleChange={(value) => {
                  if (value) setActivityType(value);
                }}
                direction={false}
                getDisplayText={getActivityTypeLabel}
                getOptionDisplayText={getActivityTypeLabel}
              />
            </ActivitySelectWrapper>
          </FieldGroup>
        </FieldsContainer>

        <ConsentContainer>
          <CheckboxComp>
            <UniversalCheckbox
              checked={requiredOver14}
              onChange={() => setRequiredOver14((checked) => !checked)}
            />
            <CheckboxText>
              {getModalMessage('CONSENT_REQUIRED_LABEL')} {getModalMessage('CONSENT_AGE')}
            </CheckboxText>
          </CheckboxComp>
          <CheckboxComp>
            <UniversalCheckbox
              checked={requiredTerms}
              onChange={() => setRequiredTerms((checked) => !checked)}
            />
            <CheckboxText>
              {getModalMessage('CONSENT_REQUIRED_LABEL')} {getModalMessage('CONSENT_TERMS_BEFORE')}
              <NavLink onClick={(event) => openPolicy(event, '/policy/terms')}>
                {getModalMessage('CONSENT_TERMS_LINK')}
              </NavLink>
              {getModalMessage('CONSENT_TERMS_AFTER')}
            </CheckboxText>
          </CheckboxComp>
          <CheckboxComp>
            <UniversalCheckbox
              checked={requiredPrivacy}
              onChange={() => setRequiredPrivacy((checked) => !checked)}
            />
            <CheckboxText>
              {getModalMessage('CONSENT_REQUIRED_LABEL')} {getModalMessage('CONSENT_PRIVACY_BEFORE')}
              <NavLink onClick={(event) => openPolicy(event, '/policy/privacy')}>
                {getModalMessage('CONSENT_PRIVACY_LINK')}
              </NavLink>
              {getModalMessage('CONSENT_PRIVACY_AFTER')}
            </CheckboxText>
          </CheckboxComp>
          <CheckboxComp>
            <UniversalCheckbox
              checked={optionalPromotion}
              onChange={() => setOptionalPromotion((checked) => !checked)}
            />
            <CheckboxText>
              {getModalMessage('CONSENT_OPTIONAL_LABEL')} {getModalMessage('CONSENT_PROMO')}
            </CheckboxText>
          </CheckboxComp>
        </ConsentContainer>

        <SubmitButton onClick={handleSubmit} disabled={isLoading > 0}>
          {getBtnName('REGISTER')}
        </SubmitButton>
        <Divider dividerType={DividerType.PLAIN} bottom="24px" />
      </MainContainer>

      <SubContainer>
        <ToggleText onClick={openLoginModal}>
          <span>{getModalMessage('REGISTER_HAVE_ACCOUNT')}</span>
          <LoginText>{getBtnName('LOGIN')}</LoginText>
        </ToggleText>
      </SubContainer>
    </RegisterMobileComp>
  );
};

const RegisterMobileComp = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  max-height: calc(100dvh - 40px);
  flex-direction: column;
  padding: 4px;
  overflow-y: auto;
  outline: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MainContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: clamp(18px, 2.5vh, 40px);
  margin-bottom: clamp(24px, 5vh, 40px);
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.H_03};
`;


const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const HighlightText = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;

const FieldLabel = styled.h3`
  margin-bottom: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const ActivitySelectWrapper = styled.div`
  width: clamp(110px, 32vw, 150px);
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const ConsentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxComp = styled.div`
  display: flex;
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};

  input {
    flex: 0 0 12px;
    margin-top: 3px;
  }
`;

const CheckboxText = styled.span`
  min-width: 0;
  flex: 1;
  line-height: 1.5;
`;

const NavLink = styled.span`
  margin: 0 3px;
  text-decoration: underline;
  cursor: pointer;
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 22px 0 15px;
`;

const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ToggleText = styled.div`
  display: flex;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const LoginText = styled.span`
  font-style: italic;
  text-decoration: underline;
`;

export default RegisterMobile;
